import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { User } from '../_models/user.model';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { CompanyModel, OfficeModel } from '../../management';
import { environment } from 'src/environments/environment';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { isEqual, uniqWith } from 'lodash-es';
import { AuthServer } from '../../enums';
import { AuthService } from './auth.service';
import { filter } from 'rxjs/operators';
import { currentAuthToken, currentUser } from '../_selectors/auth.selectors';

@Injectable({ providedIn: 'root' })
export class UserService {
  public currentUser: Observable<User>;
  private authToken;
  currentUserId;

  constructor(
    private auth: AuthService,
    private httpClient: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store
      .pipe(
        select(currentUser),
        filter((res) => !!res.id),
      )
      .subscribe((res) => {
        this.currentUserId = res.id;
      });
  }

  createUser(user: User) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const userName = user.userName;
    const isActive = user.isActive;
    const agent = user.agent;
    const companyID = user.companyID;
    const email = user.email;
    const password = user.password;
    const firstName = user.firstName;
    const lastName = user.lastName;
    const roles = user.roles;
    const primaryOfficeId = user.primaryOffice;
    const otherOfficeIds = user.allowedOffices;
    const addProfile = user.addProfile ? user.addProfile : false;
    return this.httpClient.post<User>(
      environment.USER_BASE + '/c-user',
      {
        userName,
        firstName,
        lastName,
        isActive,
        email,
        password,
        companyID,
        primaryOfficeId,
        otherOfficeIds,
        roles,
        addProfile,
        agent,
      },
      { headers: httpHeaders },
    );
  }

  getUserById(userId?: number): Observable<User> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    httpHeaders.set('access-control-allow-methods', 'POST');
    return this.httpClient.post<User>(
      environment.USER_BASE + '/user-by-id',
      { id: userId },
      { headers: httpHeaders },
    );
  }

  getUsersById(usersId: number[]): Observable<QueryResultsModel> {
    let fromString = '';
    usersId.forEach((id) => (fromString += `&id=${id}`));

    const params = new HttpParams({ fromString });

    return this.httpClient.get<QueryResultsModel>(`${environment.USER_BASE}/users-by-ids`, {
      params,
    });
  }

  getUserByUserName(userName?: any[]): Observable<any> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    httpHeaders.set('access-control-allow-methods', 'POST');

    let param = '';
    userName = uniqWith(userName, isEqual);
    userName.forEach((element) => (param = param + '&name=' + element));
    const params = new HttpParams({ fromString: param });

    return this.httpClient.get<any>(environment.USER_BASE + '/users-by-ids', {
      headers: httpHeaders,
      params,
    });
  }

  updatStatusSingle(id?: number, isActive?: boolean) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    return this.httpClient.put<any>(
      environment.USER_BASE + '/userstatusupdatesingle',
      {
        id,
        isActive,
      },
      { headers: httpHeaders },
    );
  }

  deleteUser(id?: number) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders.set('access-control-allow-methods', 'POST');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);

    return this.httpClient.delete<any>(environment.USER_BASE + '/user/' + id, {
      headers: httpHeaders,
    });
  }

  deleteUsers(ids: any[]) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders.set('access-control-allow-methods', 'POST');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    return this.httpClient.post<any>(
      environment.USER_BASE + '/multiple-soft-delete-user',
      { ids },
      { headers: httpHeaders },
    );
  }

  updateStatusForUsers(ids?: User[], isActive?: number) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders.set('access-control-allow-methods', 'POST');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const idsForUpdate: any[] = [];
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < ids.length; i++) {
      idsForUpdate.push(ids[i].id);
    }
    const body = {
      ids: idsForUpdate,
      isActive,
    };
    return this.httpClient.put<any>(environment.USER_BASE + '/userstatusupdate', body, {
      headers: httpHeaders,
    });
  }

  updateUser(_user: User): Observable<any> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const userName = _user.userName;
    const email = _user.email;
    const firstName = _user.firstName;
    const lastName = _user.lastName;
    const roles = _user.roles;
    const primaryOfficeId = _user.primaryOffice;
    const otherOfficeIds = _user.allowedOffices;
    const isActive = _user.isActive;
    const agent = _user.agent;
    const id = this.currentUserId === _user.id ? null : _user.id;
    return this.httpClient.put(
      environment.USER_BASE + '/user',
      {
        userName,
        lastName,
        firstName,
        email,
        isActive,
        id,
        roles,
        primaryOfficeId,
        otherOfficeIds,
        agent,
      },
      { headers: httpHeaders },
    );
  }

  findUsers(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const body = {
      pageSize: queryParams.filter.pageSize,
      pageIndex: queryParams.filter.pageIndex,
      searchData: queryParams.filter.searchData,
      isActive: queryParams.filter.isActive,
    };

    let sort = queryParams.filter.sortActive;

    if (sort === 'isActive') sort = 'is_active';
    if (sort === 'company') sort = 'company.name';

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);

    let fromString = '';

    if (queryParams.filter.searchNameEmail)
      fromString += /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(queryParams.filter.searchNameEmail)
        ? `&email_has=${queryParams.filter.searchNameEmail}`
        : `&name_has=${queryParams.filter.searchNameEmail}`;
    if (queryParams.filter.searchUsername)
      fromString += `&username_has=${queryParams.filter.searchUsername}`;
    if (queryParams.filter.searchCompany)
      fromString += `&company_id=${queryParams.filter.searchCompany}`;
    if (queryParams.filter.isAgent) fromString += `&is_agent=${queryParams.filter.isAgent}`;
    if (queryParams.filter.searchOfficeName)
      fromString += `&office_has=${queryParams.filter.searchOfficeName}`;
    const params = new HttpParams({ fromString });

    return this.httpClient.post<QueryResultsModel>(
      environment.USER_BASE +
        '/all-users' +
        '?sort=' +
        sort +
        `,${queryParams.filter.sortDirection}`,
      body,
      { headers: httpHeaders, params },
    );
  }

  getUsersAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const { filter, sortOrder, sortField: originalSortField } = queryParams;

    let sortField = originalSortField;
    if (sortField === 'isActive') sortField = 'is_active';
    if (sortField === 'company') sortField = 'company.name';

    let fromString = '';

    if (filter.searchData) fromString += `&search_term=${filter.searchData}`;
    if (filter.isActive) fromString += `&is_active=${filter.isActive}`;
    if (filter.searchNameEmail)
      fromString += /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(filter.searchNameEmail)
        ? `&email_has=${filter.searchNameEmail}`
        : `&name_has=${filter.searchNameEmail}`;
    if (filter.searchUsername) fromString += `&username_has=${filter.searchUsername}`;
    if (filter.searchCompany) fromString += `&company_id=${filter.searchCompany}`;

    const params = new HttpParams({ fromString });

    return this.httpClient.get(
      `${environment.USER_BASE}/users/csv?sort=${sortField},${sortOrder}`,
      { params, responseType: 'text' },
    );
  }
  searchUser(categoryId, searchTerm) {
    return this.searchUsers(searchTerm, categoryId);
  }

  userAutocomplete(searchTerm) {
    return this.searchUsers(searchTerm);
  }

  searchUsers(searchTerm: string, categoryId = null): Observable<any> {
    let httpHeaders = new HttpHeaders();
    const category = categoryId ? '&category_id=' + categoryId : '';
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);

    return this.httpClient.get<any>(
      environment.USER_BASE +
        '/autocomplete-user?is_active=true' +
        category +
        '&term=' +
        searchTerm,
      {
        headers: httpHeaders,
      },
    );
  }

  changePassword(user_id?: any, oldPassword?: any, newPassword?: any, confirmPassword?: any) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    let body = {};
    body = { oldPassword, newPassword, confirmPassword };
    return this.httpClient.put(environment.USER_BASE + `/users/${user_id}/password`, body, {
      headers: httpHeaders,
    });
  }

  getAllCompanies(): Observable<CompanyModel[]> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    return this.httpClient.get<CompanyModel[]>(environment.USER_BASE + '/company-no-admin', {
      headers: httpHeaders,
    });
  }

  getAllOffices(officeIds?: number[]): Observable<OfficeModel[]> {
    let httpHeaders = new HttpHeaders();
    let fromString = '';

    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);

    if (officeIds?.length)
      officeIds.forEach((id) => {
        if (id) fromString += `&id=${id}`;
      });

    const params = new HttpParams({ fromString });

    return this.httpClient.get<OfficeModel[]>(environment.USER_BASE + '/all-office', {
      headers: httpHeaders,
      params,
    });
  }

  changeProfilePic(_user: any): Observable<any> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    return this.httpClient.put(environment.USER_BASE + '/user', _user, { headers: httpHeaders });
  }

  getOAuthURL(authServer: AuthServer, list?, categoryId?): Observable<any> {
    let params = new HttpParams();
    const urlString = {
      [AuthServer.GOOGLE]: 'google',
      [AuthServer.MICROSOFT]: 'microsoft',
    };

    if (categoryId) {
      params = params.set('categoryId', categoryId);
      params = params.set('list', list);
    }
    params = params.set('list', list);
    return this.httpClient.get(environment.USER_BASE + `/${urlString[authServer]}_oauth_url`, {
      params,
    });
  }

  disconnectFromAuthServer(categoryId?): Observable<any> {
    const body = {
      category_id: categoryId,
    };
    return this.httpClient.put(environment.USER_BASE + '/deactivate-inbox', body);
  }

  connectFax(categoryId): Observable<any> {
    const body = {
      category_id: categoryId,
    };
    return this.httpClient.post(environment.USER_BASE + '/create_documo_user', body);
  }

  disconnectFax(categoryId): Observable<any> {
    const body = {
      category_id: categoryId,
    };
    return this.httpClient.put(environment.USER_BASE + '/delete_documo_user', body);
  }

  authenticateToNylas(code, provider, categoryId?): Observable<any> {
    const body = {
      google_code: code,
      microsoft_code: code,
      category_id: categoryId,
    };
    // tslint:disable-next-line:triple-equals
    provider == 'MICROSOFT' ? delete body.google_code : delete body.microsoft_code;
    return this.httpClient.post(environment.USER_BASE + '/authenticate_to_nylas', body);
  }
}
